<template>
  <v-card class="elevation-12" :loading="loading">
    <v-toolbar
        color="primary"
        dark
        flat
    >
      <v-toolbar-title>{{ $t('editUnitForm.title') }}</v-toolbar-title>
    </v-toolbar>
    <v-spacer class="py-2"></v-spacer>
    <v-card-text>
      <v-form>
        <v-text-field
            v-model="form.name"
            :label="$t('addGlobalUnitForm.labels.name')"
            type="text"
            maxlength="400"
            :counter="400"
            :error-messages="nameErrors"
            @keyup="form.isUnitNameExists = false"
        />



        <v-text-field
            v-model="form.address"
            :label="$t('addGlobalUnitForm.labels.address')"
            type="text"
            maxlength="600"
            :counter="600"
            :error-messages="addressErrors"
        />

        <v-text-field
            v-model="form.contractSum"
            :label="$t('addUnitForm.labels.contractSum')"
            type="number"
            step="0.01"
        ></v-text-field>

        <v-text-field
            v-model="form.sumForMail"
            :label="$t('addUnitForm.labels.sumForMail')"
            type="number"
            step="0.01"
        ></v-text-field>

        <v-text-field
            v-model="form.sumForOverPaged"
            :label="$t('addUnitForm.labels.sumForOverPaged')"
            type="number"
            step="0.01"
        ></v-text-field>

        <v-text-field
            v-model="form.contractNumber"
            :label="$t('addUnitForm.labels.contractNumber')"
            type="text"
            maxlength="400"
            :counter="400"
        />

        <v-spacer class="py-2"></v-spacer>

        <DateInput v-model="form.contractDateFrom"
                   :label="$t('addUnitForm.labels.contractDateFrom')"
                   :dense="true"
                   min-width="0"
                   max-width="80%"
        />

        <v-spacer class="py-2"></v-spacer>

        <DateInput v-model="form.contractDateTo"
                   :label="$t('addUnitForm.labels.contractDateTo')"
                   :dense="true"
                   min-width="0"
                   max-width="80%"
        />

        <v-card v-if="form.contractSignerUnitId === 4" class="mx-auto">
          <v-card-text>
            <div>{{ $t('addGlobalUnitForm.labels.regions') }}</div>
            <v-row>
              <v-col cols="12" md="12" lg="4" class="pa-0"
                     v-for="item in form.selectedRegions"
                     :key="item.region.id"
              >
                <v-checkbox
                    v-model="item.allowed"
                    class="mt-2 pa-2"
                    :label="item.region.name"
                    :hide-details="true"
                    :error-messages="regionErrors"
                    dense
                />
              </v-col>
            </v-row>
            <div v-if="!anyCheckboxSelected" class="error--text">
              {{ $t('addGlobalUnitForm.errors.regionRequired') }}
            </div>
          </v-card-text>
        </v-card>

        <v-radio-group v-if="isAdmin" v-model="form.isHead">
          <template v-slot:label>
            <strong>{{ $t('addUnitForm.unitType.title') }}</strong>
          </template>
          <v-radio :value="true" color="success">
            <template v-slot:label>
              <div>{{ $t('addUnitForm.unitType.head') }}</div>
            </template>
          </v-radio>
          <v-radio :value="false" color="secondary">
            <template v-slot:label>
              <div>{{ $t('addUnitForm.unitType.regular') }}</div>
            </template>
          </v-radio>
        </v-radio-group>

        <v-radio-group v-model="form.isActive">
          <template v-slot:label>
            <div><strong>{{ $t('addUnitForm.status.title') }}</strong></div>
          </template>
          <v-radio :value="true" color="success">
            <template v-slot:label>
              <div>{{ $t('addUnitForm.status.active') }}</div>
            </template>
          </v-radio>
          <v-radio :value="false" color="secondary">
            <template v-slot:label>
              <div>{{ $t('addUnitForm.status.inactive') }}</div>
            </template>
          </v-radio>
        </v-radio-group>

        <v-snackbar v-model="form.isUnitNameExists"
                    :timeout="5000"
                    :top="true"
                    color="error"
        >
          {{ $t('addGlobalUnitForm.messages.unitExists') }}
        </v-snackbar>

        <v-snackbar v-model="isServerError"
                    :timeout="5000"
                    :top="true"
                    color="error"
        >
          {{ $t('addGlobalUnitForm.messages.serverError') }}
        </v-snackbar>
      </v-form>
    </v-card-text>
    <v-card-actions>
      <v-btn text color="secondary" @click="onCancelButtonClicked">{{ $t('addGlobalUnitForm.actions.cancel') }}</v-btn>
      <v-spacer/>
      <v-btn text color="primary" :disabled="!allowSave" @click="onSaveButtonClicked">{{ $t('editUnitForm.actions.save') }}</v-btn>
    </v-card-actions>
  </v-card>
</template>

<script>
import {maxLength, minLength, required} from "vuelidate/lib/validators";
import {LAYOUT_LOADING_START, LAYOUT_LOADING_STOP} from "@/store/modules/layout/actions";
import UnitService from '@/services/unit.service'
import _ from "underscore";
import DateInput from "@/components/input/DateInput.vue";
import {Admin} from "../../enums/user.type";

export default {
  name: "EditUnitComponent",
  components: {DateInput},
  props: {
    allowedRegions: {
      type: Array,
      default: () => {
        return []
      }
    }
  },
  data() {
    return {
      loading: false,
      unit: null,

      form: {
        id: '',
        name: '',
        isHead: false,
        address: '',
        organizationInn: '',
        contractSignerUnitId: '', // To'g'ri nomlandi
        selectedRegions: [],
        contractSum: null,
        sumForMail: null,
        sumForOverPaged: null,

        contractNumber: '',
        contractDateFrom: null,
        contractDateTo: null,
        isActive: false,

        isUnitNameExists: false,
        isTryChangeHeadUnit: false
      },
      isServerError: false
    }
  },
  computed: {
    nameErrors() {
      const errors = []
      if (this.$v.form.name.$dirty) {
        if (!this.$v.form.name.required) {
          errors.push(this.$t('addGlobalUnitForm.errors.nameRequired'))
        } else {
          !this.$v.form.name.maxLength && errors.push(this.$t('addGlobalUnitForm.errors.nameTooLong'))
        }
      }
      return errors
    },
    addressErrors() {
      const errors = []
      if (this.$v.form.address.$dirty) {
        if (!this.$v.form.address.required) {
          errors.push(this.$t('addGlobalUnitForm.errors.addressRequired'))
        } else {
          !this.$v.form.address.maxLength && errors.push(this.$t('addGlobalUnitForm.errors.addressTooLong'))
        }
      }
      return errors
    },
    isAdmin() {
      return this.$store.getters.appIsInitialized && this.$store.getters.appLogin?.type === Admin;
    },
    regionErrors() {
      const errors = []
      if (this.$v.form.selectedRegions.$dirty) {
        if (!this.$v.form.selectedRegions.required) {
          errors.push(this.$t('addGlobalUnitForm.errors.regionRequired'))
        } else {
          !this.$v.form.selectedRegions.minLength && errors.push(this.$t('addGlobalUnitForm.errors.regionRequired'))
        }
      }
      return errors
    },
    anyCheckboxSelected() {
      return _.filter(this.form.selectedRegions, r => { return r.allowed }).length > 0
    },
    allowSave() {
      let allow = this.anyCheckboxSelected
      return allow
    }
  },
  validations: {
    form: {
      name: {
        required,
        maxLength: maxLength(400)
      },
      address: {
        required,
        maxLength: maxLength(600)
      },
      selectedRegions: {
        required,
        minLength: minLength(1)
      }
    }
  },
  methods: {
    set(unit) {
      this.clear()
      this.form.id = unit.id
      this.form.name = unit.name
      // Mapperga moslashtirildi:
      this.form.contractSignerUnitId = unit.organization?.contractSignerUnitId || ''
      this.form.isHead = unit.isHead
      this.form.address = unit.address
      this.form.contractSum = unit.contractSum
      this.form.sumForMail = unit.sumForMail
      this.form.sumForOverPaged = unit.sumForOverPaged
      this.form.contractNumber = unit.contractNumber
      this.form.contractDateFrom = unit.contractDateFrom
      this.form.contractDateTo = unit.contractDateTo
      this.form.isActive = unit.isActive
      this.unit = unit

      this.form.selectedRegions = _.map(this.allowedRegions, region => {
        return {
          region: region,
          allowed: !!_.find(unit.regions, r => {
            return r.id === region.id
          })
        }
      })
    },
    clear() {
      this.$v.$reset()
      this.form.name = ''
      this.form.contractSignerUnitId = '' // Tozalanmoqda
      this.form.isHead = false
      this.form.address = ''
      this.form.contractSum = null
      this.form.sumForMail = null
      this.form.sumForOverPaged = null
      this.form.contractNumber = ''
      this.form.contractDateFrom = null
      this.form.contractDateTo = null
      this.form.isActive = false
      this.unit = null
      this.form.selectedRegions = _.map(this.allowedRegions, region => {
        return {
          region: region,
          allowed: false
        }
      })
    },
    isCurrentUnitHead() {
      return this.unit.isHead;
    },
    onCancelButtonClicked() {
      this.clear()
      this.$emit('cancel')
    },
    onSaveButtonClicked() {
      this.$v.form.$touch()
      if (!this.$v.form.$invalid) {
        this.loading = true;
        this.$store.dispatch(LAYOUT_LOADING_START)

        const checkedRegions = _.filter(this.form.selectedRegions, ar => {
          return ar.allowed
        })

        this.isServerError = false
        UnitService.editUnit({
          id: this.form.id,
          unitName: this.form.name,
          organizationInn: this.unit.organization.inn,
          contractorSignerUnitId: this.form.contractSignerUnitId, // Backend kutayotgan parametrga yuborilmoqda (nomini tekshiring, odatda shunday bo'ladi)
          isHead: this.form.isHead,
          address: this.form.address,
          contractSum: this.form.contractSum,
          sumForMail: this.form.sumForMail,
          sumForOverPaged: this.form.sumForOverPaged,
          contractNumber: this.form.contractNumber,
          contractDateFrom: this.form.contractDateFrom,
          contractDateTo: this.form.contractDateTo,
          isActive: this.form.isActive,
          region: _.map(checkedRegions, ar => {
            return ar.region
          })
        })
            .then(() => {
              this.clear()
              this.$emit('updated')
            }, () => {
              this.isServerError = true
            })
            .finally(() => {
              this.loading = false
              this.$store.dispatch(LAYOUT_LOADING_STOP)
            })
      }
    }
  },
  created() {
    this.clear()
  },
  watch: {
    filters: {
      deep: true,
      handler() {
        // Bu faylda load() funksiyasi yo'q, shuning uchun bu watcher ehtimol xato ko'chirilgan bo'lishi mumkin.
        // Odatda Edit komponentida bu shart emas. Agar muammo qilsa o'chirib tashlang.
      }
    }
  }
}
</script>

<style scoped>

</style>